/**
 * StaffAuthContext — session provider for the School OS.
 * Wraps every /school/* page. Reads GET /api/staff/me on mount and
 * exposes { user, organization, permissions, roleLabel, reload, logout }.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const StaffAuthCtx = createContext(null);

export const StaffAuthProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, user: null, organization: null, permissions: [], roleLabel: '', departmentScoped: false });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/me`, { withCredentials: true });
      setState({ loading: false, user: data.user, organization: data.organization,
                 permissions: data.permissions || [], roleLabel: data.role_label || '',
                 departmentScoped: !!data.department_scoped });
    } catch {
      setState({ loading: false, user: null, organization: null, permissions: [], roleLabel: '', departmentScoped: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    try { await axios.post(`${API}/staff/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    setState({ loading: false, user: null, organization: null, permissions: [], roleLabel: '', departmentScoped: false });
    // iter640 · Clear the active-org scope so the next login/user opens
    // to their own first membership (not this user's stale one).
    try { localStorage.removeItem('flyt_active_org_id'); } catch { /* private mode */ }
    delete axios.defaults.headers.common['X-Flyt-Active-Org'];
    window.location.href = '/staff/login';
  };

  return (
    <StaffAuthCtx.Provider value={{ ...state, reload: load, logout, can: (p) => state.permissions.includes(p) }}>
      {children}
    </StaffAuthCtx.Provider>
  );
};

export const useStaffAuth = () => {
  const ctx = useContext(StaffAuthCtx);
  if (!ctx) throw new Error('useStaffAuth must be used within StaffAuthProvider');
  return ctx;
};
